import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, Renderer2, SimpleChanges, ViewChild, OnInit, inject } from '@angular/core';
import { SpinnerService } from '../../../../core/service/spinner.service';
import { ModalCountService } from "./service/modal-count.service";
import { ButtonComponent } from "../buttons/button.component";
import { NgClass } from "@angular/common";
import { AllIconsComponent } from "../all-icons/all-icons.component";

@Component({
  selector: 'app-modal-container',
  templateUrl: './modal-container.component.html',
  styleUrls: ['./modal-container.component.scss'],
  imports: [
    ButtonComponent,
    NgClass,
    AllIconsComponent
],
})
export class ModalContainerComponent implements OnChanges, OnDestroy, OnInit {
  private spinner = inject(SpinnerService);
  private renderer = inject(Renderer2);
  private countService = inject(ModalCountService);

  @Input() title: string;
  @Input() titleStyles: string;
  @Input() titleDirection: 'centered' | 'start' = 'centered';
  @Input() greenTitle: string;
  @Input() subtitle: string;
  @Input() bigSize = true; // set false if you need a small modal (without line after header and different design)
  @Input() applyActivated = true; // disables Apply button if false
  @Input() lowerLeftActivated = true; // disables Delete button if false
  @Input() upperRight2Activated = true; // disables UpperRight button if false
  @Input() innerModal = false; // set true if your Modal is inside another modal
  @Input() applyHint: string; // Hint displayed when hovering over the disabled Apply button.
  @Input() lowerLeftHint: string; // Hint displayed when hovering over the disabled lowerLeftHint button
  @Input() buttons: ModalButtons;
  @Input() showWhiteSpace = true;
  @Input() spinnerProps: SpinnerProps = {
    isSpinnerActive: false,
    notActiveOnApply: false,
  };

  @Output() closeButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() upperRight1ButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() upperRight2ButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() lowerLeftButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() applyButtonClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() dismissButtonClicked: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('spinnerDiv') spinnerDiv: ElementRef;

  backString = 'Back';

  ngOnInit(): void {
    this.countService.modalsCount$$.update((c) => c + 1);
  }

  ngOnDestroy(): void {
    this.countService.modalsCount$$.update((c) => (Math.max(0, c - 1)));

    // Is necessary to show the global scrollbar when the modal is closed
    if (this.countService.modalsCount$$() === 0) {
      this.renderer.removeStyle(document.documentElement, 'overflow');
      this.renderer.removeClass(document.body, CDK_OVERLAY_Z_INDEX_IN_MODAL);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Is necessary to hide the global scrollbar when the modal is opened
    this.renderer.setStyle(document.documentElement, 'overflow', 'hidden');
    this.renderer.addClass(document.body, CDK_OVERLAY_Z_INDEX_IN_MODAL);
    if (!changes.spinnerProps || !this.spinnerDiv) {
      return;
    }
    if (this.spinnerProps.isSpinnerActive) {
      this.spinner.startLoadingForComponent(this.spinnerDiv);
    } else {
      this.spinner.finishLoadingForComponent(this.spinnerDiv);
    }
  }

  applyButton(): void {
    if (!this.spinnerProps?.notActiveOnApply) {
      this.spinner.startLoadingForComponent(this.spinnerDiv);
    }
    this.applyButtonClicked.emit();
  }
}

const CDK_OVERLAY_Z_INDEX_IN_MODAL = 'cdk-overlay-z-index-in-modal';

export interface ModalButtons { // Object with all buttons you need in the modal, set values only to those you need
  back?: boolean; // set true if button needed
  close?: boolean; // set true if button needed
  upperRight1?: string; // set String text if button needed
  upperRight1Class?: string; // set String class if additional class for this button needed
  upperRight2?: string; // set String text if button needed
  upperRight2Class?: string; // set String class if additional class for this button needed
  lowerLeft?: string; // set String text if button needed
  lowerLeftClass?: string; // set String class if additional class for this button needed
  lowerLeftIcon?: string; // set String icon if icon needed
  lowerLeftIconSize?: string;
  manualIconSize?: string;
  dismiss?: string; // set String text if button needed
  apply?: string; // set String text if button needed
  applySize?: string; // set 'big-button-size' if big button needed
  dismissApplyAlignRight?: boolean; // set true if no lowerLeft and you need to align lower buttons right
}

export interface SpinnerProps { // spinner is automatically activated when Apply button clicked
  isSpinnerActive?: boolean; // set true to show spinner (use when clicking on other buttons)
  notActiveOnApply?: boolean; // set true if you don't need to activate spinner on Apply
}
